import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { environment } from '@quorum/environments';
import {
  AppointmentsAssociatesAddressesQueryParameters,
  VehiclesAssociatesAddressesQueryParameters,
} from '@quorum/models/xs-query';
import {
  AppointmentsAssociatesAddress,
  Associate,
  AuthenticatedUser,
  Employee,
  Vehicle,
  VehiclesAssociatesAddress,
} from '@quorum/models/xs-resource';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import moment from 'moment';
import { Observable, Subject, Subscription, forkJoin } from 'rxjs';
import { debounceTime, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
declare const noticeable: any;

@Component({
  selector: 'svc-action-bar',
  templateUrl: './action-bar.component.html',
  styleUrls: ['./action-bar.component.scss'],
})
export class ActionBarComponent implements OnInit {
  @Input() employee: Employee;
  @Input() authenticatedUser: AuthenticatedUser;
  @Input() showSettings = false;
  @Output() emitUserLogout: EventEmitter<void> = new EventEmitter();
  @Output() emitOpenSettings: EventEmitter<void> = new EventEmitter();
  constructor(
    private apiService: ApiService,
    private authenticationStateService: AuthenticationStateService,
    private fb: FormBuilder,
    private router: Router,
    private systemControlStateService: SystemControlStateService
  ) {}

  vehicleSearchForm: FormGroup = this.fb.group({
    search: [],
  });
  vehicleSearchResults: Vehicle[];
  appointmentSearchResults: AppointmentsAssociatesAddress[];
  associateSearchResults: Associate[];
  loadingSearchResults = false;
  authEmployee$: Observable<Employee>;
  vehicleSearchFormSearchSub: Subscription;
  destroy$ = new Subject();
  showMobileSearchBar = false;

  ngOnInit(): void {
    this.authenticationStateService
      .selectAuthenticatedEmployee()
      .pipe(
        filter((emp) => emp != null),
        take(1)
      )
      .subscribe(() => {
        this.vehicleSearchFormSearchSub = this.vehicleSearchForm
          .get('search')
          .valueChanges.pipe(
            debounceTime(300),
            tap(() => {
              this.vehicleSearchResults = [];
              this.appointmentSearchResults = [];
              this.loadingSearchResults = false;
            }),
            filter((value: string) => value && value != null && value.trim() !== ''),
            switchMap((value: string) => {
              this.loadingSearchResults = true;
              const vehicleQueryParams = new VehiclesAssociatesAddressesQueryParameters({
                statusCode: '2,3,4,I',
                search: value,
              });
              const appointmentQueryParams = new AppointmentsAssociatesAddressesQueryParameters({
                search: value,
                appointmentDateFrom: moment().subtract(6, 'months').format(),
                statusId: '1,2',
                department: 'M',
              });
              return forkJoin([
                this.apiService.get(`/v/1/reporting/vehicles/vehicles-associates-addresses`, {
                  params: vehicleQueryParams,
                }),
                this.apiService.get(`/v/1/reporting/fixed-operations/appointments-associates-addresses`, {
                  params: appointmentQueryParams,
                }),
              ]).pipe(
                map(([vehicles, appointments]) => {
                  this.vehicleSearchResults = vehicles;
                  this.appointmentSearchResults = appointments;
                  this.loadingSearchResults = false;
                })
              );
            }),
            takeUntil(this.destroy$)
          )
          .subscribe();
      });
  }

  ngAfterViewInit() {
    this.authenticationStateService
      .generateJWTTokenForNoticeable(this.authenticatedUser)
      .pipe(take(1))
      .subscribe((ssoToken: any) => {
        noticeable.identify(environment.noticeable.sharedProjectId, ssoToken.endUserSsoToken);
        noticeable.render('widget', environment.noticeable.iconWidgetIdPowerLane, { selector: '#noticeable-icon' });
      });
  }

  logout() {
    this.emitUserLogout.emit();
  }

  resetForm() {
    this.vehicleSearchForm.get('search').setValue(null);
  }

  selectVehicle(result: VehiclesAssociatesAddress) {
    this.router.navigate(['vehicle', result ? (result.vehicleId ? result.vehicleId : result.id) : 'new']);
  }

  selectAppointment(appointment: AppointmentsAssociatesAddress) {
    if (appointment.vehicleId) {
      this.router.navigate([
        `vehicle/${appointment.vehicleId}/appointments/${
          appointment.appointmentId ? appointment.appointmentId : appointment.id
        }`,
      ]);
    } else {
      this.router.navigate([`freeform/appointments/${appointment.appointmentId}`]);
    }
  }

  ngOnDestroy() {
    noticeable.destroy('widget', environment.noticeable.iconWidgetIdPowerLane);
    this.destroy$.next();
    this.destroy$.complete();
  }

  openSettings() {
    this.emitOpenSettings.emit();
  }
}
